export type CategoryType =
  | 'needs'
  | 'comfort'
  | 'education'
  | 'goals'
  | 'freedom';

export interface Tag {
  id: string;
  name: string;
  icon: string;
  createdAt: Date;
  userId?: string;
}

export const PUBLIC_TAGS: Tag[] = [
  { id: 'public-mercado', name: 'Mercado', icon: '🛒', createdAt: new Date(0) },
  { id: 'public-academia', name: 'Academia', icon: '🏋️', createdAt: new Date(0) },
  { id: 'public-combustivel', name: 'Combustível', icon: '⛽', createdAt: new Date(0) },
  { id: 'public-farmacia', name: 'Farmácia', icon: '💊', createdAt: new Date(0) },
  { id: 'public-contas', name: 'Contas', icon: '📄', createdAt: new Date(0) },
];

export interface Expense {
  id: string;
  createdAt: Date;
  date: Date;
  title: string;
  description: string;
  value: number;
  categoryType: CategoryType;
  userId: string;
  tags?: string[];
}

export interface Income {
  id: string;
  title: string;
  createdAt: Date;
  month: number;
  year: number;
  value: number;
  userId: string;
}

export interface Budget {
  id: string;
  month: number;
  year: number;
  needs: number;
  comfort: number;
  education: number;
  goals: number;
  freedom: number;
  userId: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  photoUrl?: string;
}

export interface SharedWallet {
  id: string;
  users: string[];
  createdAt: Date;
}

export interface Asset {
  id: string;
  title: string;
  userId: string;
  createdAt: Date;
  deactivated?: boolean;
}

export interface AssetPayment {
  id: string;
  assetId: string;
  userId: string;
  value: number;
  day: number;
  month: number;
  year: number;
  createdAt: Date;
}

export interface CaixinhaSnapshot {
  id: string;
  date: Date;
  value: number;
}

export interface CaixinhaLog {
  id: string;
  date: Date;
  value: number; // positive = entrada, negative = saída
  description: string;
}

export interface Caixinha {
  id: string;
  userId: string;
  title: string;
  description?: string;
  createdAt: Date;
  snapshots: CaixinhaSnapshot[];
  log: CaixinhaLog[];
}

export interface BtcApplication {
  id: string;
  userId: string;
  date: Date;
  btcAmount: number;
  paidBrl: number;
  feeBtc: number;
  createdAt: Date;
}

export interface WishlistItem {
  id: string;
  title: string;
  links?: string[];
  averagePrice: number;
  targetDate: Date;
  order: number;
  createdAt: Date;
  addedBy: string;
  completed?: boolean;
  completedAt?: Date;
}

// ============================================
// INVESTMENTS
// ============================================

export type InvestmentType = 'acao' | 'fii' | 'tesouro';

/**
 * Tipos de provento. O tratamento no IRPF difere:
 * - dividendo / rendimento_fii: rendimento isento
 * - jcp: tributação exclusiva na fonte (15% retido)
 * - amortizacao / restituicao_capital: NÃO é rendimento, reduz o custo médio
 * - bonificacao: aumenta a quantidade pelo custo informado pela empresa
 */
export type EarningKind =
  | 'dividendo'
  | 'jcp'
  | 'rendimento_fii'
  | 'amortizacao'
  | 'restituicao_capital'
  | 'bonificacao';

export interface InvestmentAsset {
  id: string;
  userId: string;
  ticker: string;
  type: InvestmentType;
  name: string;
  cnpj?: string;
  currentPrice?: number;
  priceUpdatedAt?: Date;
  deactivated?: boolean;
  createdAt: Date;
}

export interface Trade {
  id: string;
  userId: string;
  assetId: string;
  operation: 'buy' | 'sell';
  date: Date;
  quantity: number;
  unitPrice: number;
  /** Corretagem + emolumentos + taxas. Entra no custo na compra, abate na venda. */
  fees: number;
  /** IRRF "dedo-duro" (0,005% sobre a venda), compensável no ajuste anual. */
  irrfWithheld?: number;
  notes?: string;
  createdAt: Date;
}

export interface Earning {
  id: string;
  userId: string;
  assetId: string;
  kind: EarningKind;
  /** Data do crédito em conta — é o que define o ano-base do IRPF. */
  paymentDate: Date;
  /** Quantidade que gerou o provento (informativo). */
  quantityBase?: number;
  valuePerShare?: number;
  grossValue: number;
  /** IRRF retido (JCP: 15%). */
  irrfWithheld: number;
  createdAt: Date;
}

/**
 * Conversão de um papel em outro carregando o custo: incorporação de fundo,
 * troca de ticker, cisão. Não realiza ganho — por isso não é uma venda
 * seguida de compra, que inflaria as vendas do mês na apuração.
 */
export interface Conversion {
  id: string;
  userId: string;
  fromAssetId: string;
  toAssetId: string;
  date: Date;
  /** Cotas que saem do papel antigo. */
  fromQuantity: number;
  /** Cotas que entram no papel novo, com o mesmo custo. */
  toQuantity: number;
  notes?: string;
  createdAt: Date;
}

/** Desdobramento (split) ou grupamento (reverse split). */
export interface CorporateAction {
  id: string;
  userId: string;
  assetId: string;
  date: Date;
  /** > 1 desdobramento (1:factor), < 1 grupamento. */
  factor: number;
  notes?: string;
  createdAt: Date;
}

export const investmentTypes: Record<
  InvestmentType,
  { label: string; shortLabel: string; color: string }
> = {
  acao: { label: 'Ações', shortLabel: 'Ação', color: '#2B6CB0' },
  fii: { label: 'Fundos Imobiliários', shortLabel: 'FII', color: '#2A9D8F' },
  tesouro: { label: 'Tesouro Direto', shortLabel: 'Tesouro', color: '#6A4C93' },
};

/** Cor do bitcoin na alocação do patrimônio — não é um InvestmentType. */
export const BITCOIN_COLOR = '#D97706';

/**
 * Renda fixa não entra na apuração mensal de renda variável: o IR do Tesouro
 * é retido na fonte pela corretora, pela tabela regressiva (22,5% a 15%).
 */
export const VARIABLE_INCOME_TYPES: InvestmentType[] = ['acao', 'fii'];

export const earningKinds: Record<
  EarningKind,
  {
    label: string;
    /** Como entra na apuração: rendimento isento, tributado na fonte, ou redução de custo. */
    treatment: 'isento' | 'exclusiva' | 'custo' | 'quantidade';
    appliesTo: InvestmentType[];
  }
> = {
  dividendo: { label: 'Dividendo', treatment: 'isento', appliesTo: ['acao'] },
  jcp: { label: 'JCP', treatment: 'exclusiva', appliesTo: ['acao'] },
  rendimento_fii: {
    label: 'Rendimento (FII)',
    treatment: 'isento',
    appliesTo: ['fii'],
  },
  amortizacao: {
    label: 'Amortização',
    treatment: 'custo',
    appliesTo: ['fii'],
  },
  restituicao_capital: {
    label: 'Restituição de capital',
    treatment: 'custo',
    appliesTo: ['acao', 'fii'],
  },
  bonificacao: {
    label: 'Bonificação em ações',
    treatment: 'quantidade',
    appliesTo: ['acao'],
  },
};

/**
 * Códigos das fichas da DIRPF. A Receita altera os códigos entre
 * exercícios — confira o layout do ano antes de transcrever.
 */
export const IRPF_CODES = {
  /** Bens e Direitos: Grupo 03 (participações societárias), código 01 (ações). */
  bensAcao: { grupo: '03', codigo: '01', label: 'Ações (inclusive as listadas em bolsa)' },
  /** Bens e Direitos: Grupo 07 (fundos), código 03 (FII). */
  bensFii: { grupo: '07', codigo: '03', label: 'Fundos de Investimento Imobiliário' },
  /** Bens e Direitos: Grupo 04 (aplicações), código 02 (títulos públicos). */
  bensTesouro: {
    grupo: '04',
    codigo: '02',
    label: 'Títulos públicos e privados sujeitos à tributação',
  },
  /** Rendimentos Isentos e Não Tributáveis: lucros e dividendos recebidos. */
  isentoDividendo: { codigo: '09', label: 'Lucros e dividendos recebidos' },
  /** Rendimentos Isentos e Não Tributáveis: rendimentos de FII. */
  isentoFii: { codigo: '26', label: 'Outros rendimentos isentos (rendimentos de FII)' },
  /** Tributação Exclusiva na Fonte: JCP. */
  exclusivaJcp: { codigo: '10', label: 'Juros sobre capital próprio' },
} as const;

/** Isenção mensal de vendas de ações no swing trade. */
export const STOCK_MONTHLY_EXEMPTION = 20000;

/**
 * Alíquotas de ganho de capital em renda variável (swing trade). O Tesouro
 * fica de fora: o imposto já sai retido na fonte, sem DARF a recolher.
 */
export const CAPITAL_GAINS_RATE: Record<InvestmentType, number> = {
  acao: 0.15,
  fii: 0.20,
  tesouro: 0,
};

/** DARF abaixo desse valor não é paga: acumula para o mês seguinte. */
export const DARF_MINIMUM = 10;

/** Código da DARF para ganhos líquidos em renda variável — pessoa física. */
export const DARF_CODE = '6015';

export interface CategoryFormData {
  name: string;
  type: CategoryType;
  icon?: string;
  color?: string;
}

/**
 * Proporções padrão do orçamento:
 * - 45% Custos Fixos (needs)
 * - 15% Conforto e Lazer (comfort)
 * - 10% Conhecimento (education)
 * - 15% Metas (goals)
 * - 15% Liberdade Financeira (freedom)
 */
export const defaultBudgetProportions: Record<CategoryType, number> = {
  needs: 45,
  comfort: 15,
  education: 10,
  goals: 15,
  freedom: 15,
};

export const categoryTypes: Record<
  CategoryType,
  {
    name: CategoryType;
    color: string;
    bgColor: string;
    label: string;
  }
> = {
  needs: {
    name: 'needs',
    color: '#4A6FA5',
    bgColor: 'bg-[#4A6FA5]',
    label: 'Custos Fixos',
  },
  comfort: {
    name: 'comfort',
    color: '#F4A261',
    bgColor: 'bg-[#F4A261]',
    label: 'Conforto e Lazer',
  },
  education: {
    name: 'education',
    color: '#2A9D8F',
    bgColor: 'bg-[#2A9D8F]',
    label: 'Conhecimento',
  },
  goals: {
    name: 'goals',
    color: '#6A4C93',
    bgColor: 'bg-[#6A4C93]',
    label: 'Metas',
  },
  freedom: {
    name: 'freedom',
    color: '#C9A227',
    bgColor: 'bg-[#C9A227]',
    label: 'Liberdade Financeira',
  },
};
